/**
 * Clinic handoff for peptide orders (SCRUM-2200).
 *
 * A clinic asked how the weekend desk knows, for a patient standing in front of
 * them, whether the order is paid, whether the vial has been reconstituted and
 * whether it has been picked up. Payment is read from the order's charge
 * ledger; the other two are staff stamps in metadata.handoff, written by
 * POST …/integration-orders/{id}/handoff. Re-order due is covered elsewhere
 * (lib/peptideOrder).
 *
 * Pure helpers only: the table, the detail modal and the Peptides page's
 * "Ready for pickup" tile all read through these, so they cannot disagree.
 */
import type { IntegrationOrder, OrderHandoff, OrderHandoffStamp } from '@/services/integrations.service';

export type PaymentChipTone = 'amber' | 'green' | 'slate';

export interface PaymentChip {
  label: string;
  tone: PaymentChipTone;
  /** Hover text — names the paid date when there is one. */
  tip: string;
}

/** "Oct 25" — the compact date the chips show. '' for an unparseable value. */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * The $ chip. Read-only and derived:
 *  - awaiting_payment (the payment gate is holding it)  → Due (amber)
 *  - charge paid / collected                             → Paid (green)
 *  - charge comped (membership covers it)                → Comped (slate)
 *  - charge logged (settled with the clinic monthly)     → Clinic billed (slate)
 *  - charge pending                                      → Due (amber)
 *  - no charge row / unknown                             → no chip
 */
export function paymentChip(order: Pick<IntegrationOrder, 'fulfillmentStatus' | 'paymentStatus' | 'paidAt'>): PaymentChip | null {
  if (order.fulfillmentStatus === 'awaiting_payment') {
    return { label: 'Due', tone: 'amber', tip: 'Payment due — the order is held and has not been sent to the pharmacy until the fee is collected' };
  }
  switch (order.paymentStatus) {
    case 'paid':
    case 'collected': {
      const when = shortDate(order.paidAt);
      return { label: 'Paid', tone: 'green', tip: when ? `Paid ${when}` : 'Paid' };
    }
    case 'comped':
      return { label: 'Comped', tone: 'slate', tip: 'Covered by the patient’s membership — nothing to collect' };
    case 'logged':
      return {
        label: 'Clinic billed',
        tone: 'slate',
        tip: 'Settled with the clinic on its monthly statement — the patient is not charged through this order',
      };
    case 'pending':
      return { label: 'Due', tone: 'amber', tip: 'Payment not collected yet' };
    default:
      return null;
  }
}

/** The order's handoff stamps: the list field, else metadata.handoff, else none. */
export function orderHandoff(order: Pick<IntegrationOrder, 'handoff' | 'metadata'>): OrderHandoff {
  if (order.handoff && typeof order.handoff === 'object') return order.handoff;
  const m = (order.metadata as { handoff?: OrderHandoff } | null | undefined)?.handoff;
  return m && typeof m === 'object' ? m : {};
}

/**
 * Delivered to the clinic rather than the patient. The server decides
 * (shipsToClinic on every list row — it also recognises older Axiom orders by
 * their "c/o <facility>" line); the fallbacks cover a row from an older API.
 */
export function shipsToClinic(order: Pick<IntegrationOrder, 'shipsToClinic' | 'items' | 'metadata'>): boolean {
  if (typeof order.shipsToClinic === 'boolean') return order.shipsToClinic;
  if ((order.items ?? []).some((i) => i.shipType === 'ship_to_clinic')) return true;
  const ship = (order.metadata as { shippingAddress?: { shipToClinic?: unknown } } | null | undefined)?.shippingAddress;
  return ship?.shipToClinic === true;
}

const DELIVERED = new Set(['completed', 'delivered']);

function cancelled(order: Pick<IntegrationOrder, 'fulfillmentStatus' | 'status'>): boolean {
  return order.fulfillmentStatus === 'cancelled' || order.fulfillmentStatus === 'rejected' || order.status === 'cancelled';
}

/**
 * Pickup tracking starts here. Orders placed before it could never carry a
 * pickedUp stamp, so counting them would fill "Ready for pickup" on deploy with
 * every clinic order delivered since "c/o <facility>" orders began (2026-09-30)
 * — almost all long since collected.
 */
export const PICKUP_TRACKED_SINCE = '2026-10-26T00:00:00Z';

function placedBeforeTracking(order: Pick<IntegrationOrder, 'createdAt'>): boolean {
  const placed = order.createdAt ? Date.parse(order.createdAt) : NaN;
  return !Number.isNaN(placed) && placed < Date.parse(PICKUP_TRACKED_SINCE);
}

/**
 * Waiting at the front desk: shipped to the clinic, delivered, not yet picked
 * up, not cancelled, and placed since pickup tracking began. The Peptides
 * page's "Ready for pickup" tile.
 */
export function isReadyForPickup(order: IntegrationOrder): boolean {
  return shipsToClinic(order)
    && DELIVERED.has(order.fulfillmentStatus ?? '')
    && !cancelled(order)
    && !placedBeforeTracking(order)
    && !orderHandoff(order).pickedUp;
}

/** Whether a handoff step can be toggled on this order (the server's rule). */
export function canToggleHandoff(order: Pick<IntegrationOrder, 'fulfillmentStatus' | 'status'>): boolean {
  return !cancelled(order);
}

/** "Reconstituted Oct 24 by Megan W." — the chip's hover text for a done step. */
export function stampTip(label: string, stamp: OrderHandoffStamp): string {
  const when = shortDate(stamp.at);
  return `${label}${when ? ` ${when}` : ''}${stamp.byName ? ` by ${stamp.byName}` : ''}`;
}
